import React, { useMemo } from 'react';
import { Box, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SalovareSelectField from '../common/SalovareSelectField';
import { FIELD_PLACEHOLDER_COLOR, buildAdornmentTextFieldSx } from '../common/formFieldStyles';
import { getCurrencySymbol } from '../../utils/currencyUtils';
import { parseDurationToMinutes } from '../../utils/appointmentBookingUtils';
import {
  ServiceValueMode,
  buildDurationMinuteOptions,
  formatClockMinutes,
  formatServicePriceLabel,
  normalizeServiceValueMode,
} from '../../utils/serviceValueFormat';
const AppointmentLineQuoteFields = ({
  service,
  quote,
  onChange,
  canSetPrice,
  currency,
  serviceName,
  priceHint,
}) => {
  const { t } = useTranslation();
  const durationOptions = useMemo(() => buildDurationMinuteOptions(), []);
  if (!service || service.isCombo) return null;

  const durationMode = normalizeServiceValueMode(service.durationMode);
  const priceMode = normalizeServiceValueMode(service.priceMode);
  const catalogMinutes = parseDurationToMinutes(service.duration);
  const durationOptional = durationMode === ServiceValueMode.Fixed && catalogMinutes === 0;
  const rangeLabel = durationMode === ServiceValueMode.Range
    ? `${formatClockMinutes(catalogMinutes)} - ${formatClockMinutes(parseDurationToMinutes(service.durationMax))}`
    : durationOptional
      ? formatClockMinutes(0)
      : t('managementAppointments.book.selectDuration');
  const fixedPriceHint = priceHint == null || priceHint === '' ? service.price : priceHint;
  const pricePlaceholder = priceMode === ServiceValueMode.Fixed
    ? (fixedPriceHint == null || fixedPriceHint === '' ? undefined : String(fixedPriceHint))
    : formatServicePriceLabel(service, {
      formatMoney: (amount) => `${amount}`,
      onRequestLabel: t('servicesManagement.valueMode.onRequest'),
      fromLabel: (price) => t('servicesManagement.valueMode.priceFrom', { price }),
    });

  return (
    <Box sx={{ display: 'grid', gap: 1.5, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{serviceName}</Typography>
      <SalovareSelectField
        label={t('managementAppointments.book.serviceDuration')}
        required={!durationOptional}
        displayEmpty
        value={quote?.durationMinutes || ''}
        onChange={(event) => onChange({ ...quote, durationMinutes: event.target.value })}
        renderValue={(value) => {
          if (!value) {
            return <Typography component="span" sx={{ color: FIELD_PLACEHOLDER_COLOR }}>{rangeLabel}</Typography>;
          }
          return durationOptions.find((option) => option.value === Number(value))?.label || value;
        }}
      >
        {durationOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
        ))}
      </SalovareSelectField>
      {canSetPrice ? (
        <TextField
          type="number"
          value={quote?.unitPrice ?? ''}
          placeholder={pricePlaceholder}
          onChange={(event) => onChange({ ...quote, unitPrice: event.target.value, priceTouched: true })}
          inputProps={{
            min: 0,
            step: 0.01,
            'aria-label': t('managementAppointments.book.servicePrice'),
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">{getCurrencySymbol(currency)}</InputAdornment>,
          }}
          sx={buildAdornmentTextFieldSx(false)}
          fullWidth
        />
      ) : null}
    </Box>
  );
};

export default AppointmentLineQuoteFields;
